<script lang="ts">
  import { _ } from 'svelte-i18n';
  import XSSCircle from '@/MyFitness/TrainingForecast/XSSCircle.svelte';
  import { xssColors } from '@/Common/constants';
  import type { XSSDetails } from '@/Util/utils';

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // execution.daily_component_compliance of a report: one entry per day with the prescribed and completed Low, High and Peak XSS.
  export let days: any[] = [];

  type Cell = { date: string; day: number; prescribed: XSSDetails | null; actual: XSSDetails | null; rest: boolean; empty: boolean } | null;
  type Month = { key: string; title: string; weeks: Cell[][] };

  const details = (x: any): XSSDetails | null => (x ? { xss: x.total ?? 0, xlss: x.low ?? 0, xhss: x.high ?? 0, xpss: x.peak ?? 0 } : null);
  // A report entry's XSS ({total, low, high, peak}) and the same once converted for the circle ({xss, xlss, xhss, xpss}).
  const entrySum = (x: any) => (x ? (x.total ?? 0) + (x.low ?? 0) + (x.high ?? 0) + (x.peak ?? 0) : 0);
  const sum = (x: XSSDetails | null) => (x ? x.xss + x.xlss + x.xhss + x.xpss : 0);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // One calendar per month, weeks Monday first as the Planner shows them; the days outside the plan are left empty.
  $: months = build(days);
  function build(entries: any[]): Month[] {
    if (!entries.length) return [];
    const byDate = new Map(entries.map((e) => [e.date, e]));
    const first = new Date(entries[0].date + 'T12:00:00');
    const last = new Date(entries[entries.length - 1].date + 'T12:00:00');
    const out: Month[] = [];
    for (const m = new Date(first.getFullYear(), first.getMonth(), 1, 12); m <= last; m.setMonth(m.getMonth() + 1)) {
      const weeks: Cell[][] = [];
      let week: Cell[] = Array((m.getDay() + 6) % 7).fill(null);
      const daysInMonth = new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
      for (let day = 1; day <= daysInMonth; day++) {
        const d = new Date(m.getFullYear(), m.getMonth(), day, 12);
        const e = byDate.get(iso(d));
        week.push({ date: iso(d), day, prescribed: e && entrySum(e.prescribed) > 0 ? details(e.prescribed) : null, actual: e ? details(e.actual) : null, rest: !!e?.rest_day, empty: !e });
        if (week.length === 7) { weeks.push(week); week = []; }
      }
      if (week.length) { while (week.length < 7) week.push(null); weeks.push(week); }
      // A week with no day of the plan in it (the plan began on the last day of the month) is not drawn.
      const shown = weeks.filter((w) => w.some((c) => c && !c.empty));
      out.push({ key: iso(m).slice(0, 7), title: m.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }), weeks: shown });
    }
    return out;
  }
</script>

<!-- Two months across when the report is wide enough, one below that; the days share the width of their month. -->
<div class="@container">
  <div class="grid grid-cols-1 @[640px]:grid-cols-2 gap-x-8 gap-y-5">
    {#each months as month (month.key)}
      <div>
        <h3 class="text-sm font-medium mb-1">{month.title}</h3>
        <table class="w-full table-fixed text-xs text-center border-collapse">
          <thead>
            <tr class="text-zinc-500">
              {#each ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as d}<th class="font-normal pb-1">{$_('report.weeks.' + d)}</th>{/each}
            </tr>
          </thead>
          <tbody>
            {#each month.weeks as week}
              <tr>
                {#each week as c}
                  <td class="align-middle h-[56px] {c && c.empty ? 'opacity-30' : ''}">
                    {#if c}
                      <div class="relative flex items-center justify-center h-full">
                        {#if !c.empty && (c.prescribed || sum(c.actual) > 0)}
                          <XSSCircle small primaryXSSDetails={c.actual ?? { xss: 0, xlss: 0, xhss: 0, xpss: 0 }} primaryTitle={$_('report.calendar.completed')} targetXSSDetails={c.prescribed} secondaryTitle={$_('report.calendar.forecasted')} />
                        {:else if c.rest}
                          <span class="text-[10px] text-zinc-400">{$_('report.calendar.rest')}</span>
                        {/if}
                        <span class="absolute top-0 left-1 text-[9px] text-zinc-400">{c.day}</span>
                      </div>
                    {/if}
                  </td>
                {/each}
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/each}
  </div>
</div>
<div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
  <span>{$_('report.calendar.thirds')}</span>
  <span class="inline-flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm" style="background:{xssColors.deviationGood}" /> {$_('report.calendar.on_target')}</span>
  <span class="inline-flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm" style="background:{xssColors.deviationWarn}" /> {$_('report.calendar.close')}</span>
  <span class="inline-flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm" style="background:{xssColors.deviationAlert}" /> {$_('report.calendar.far_off')}</span>
  <span>{$_('report.calendar.click')}</span>
</div>
